import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Search.css"; // Import your custom CSS for DatePicker
import Slider from "@mui/material/Slider";
import { SliderThumb, styled } from "@mui/material";
import GoogleSearchBox from "./GoogleSearchBox";
import axios from "axios";
import { WorkspaceCardData } from "./Interfaces/WorkspaceCard";
import WorkspaceList from "./WorkspaceList";
import { AddressComponents } from "./AddressComponents";
import FeaturedWorkspaces from "./FeaturedWorkspaces";
import WorkspaceMap from "./GoogleMap";
import { useJsApiLoader } from "@react-google-maps/api";
import workspaceTypes from "../assets/WorkspaceTypes";

const AirbnbSlider = styled(Slider)(() => ({
  color: "#3b4b6e",
  height: 3,
  padding: "13px 0",
  "& .MuiSlider-thumb": {
    height: 27,
    width: 27,
    backgroundColor: "#fff",
    border: "1px solid currentColor",
    "&:hover": {
      boxShadow: "0 0 0 8px rgba(58, 133, 137, 0.16)",
    },
    "& .airbnb-bar": {
      height: 9,
      width: 1,
      backgroundColor: "currentColor",
      marginLeft: 1,
      marginRight: 1,
    },
  },
  "& .MuiSlider-track": {
    height: 3,
  },
  "& .MuiSlider-rail": {
    color: "#d8d8d8",
    opacity: 1,
    height: 3,
  },
}));

function AirbnbThumbComponent(props: React.HTMLAttributes<unknown>) {
  const { children, ...other } = props;
  return (
    <SliderThumb {...other}>
      {children}
      <span className="airbnb-bar" />
      <span className="airbnb-bar" />
      <span className="airbnb-bar" />
    </SliderThumb>
  );
}

interface WorkspaceSearchParams {
  location: string;
  moveInDate: Date | null;
  capacity: number | null;
  userId: number | null;
  userOnly?: boolean;
  workspaceTypeId?: number | null;
  minCapacity?: number | null;
  maxCapacity?: number | null;
  minimumBookingType?: number | null;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

interface PaginatedWorkspaceResponse {
  workspaces: WorkspaceCardData[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

const maxGuests = 200;
const libraries: ("places" | "geocoding" | "marker")[] = [
  "places",
  "geocoding",
  "marker",
];

export default function Search() {
  // Load Google Maps API at the top level
  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries,
    version: "weekly",
  });

  // Get navigation state from home page
  const locationState = useLocation().state as {
    searchLocation?: string;
    fromHomePage?: boolean;
  } | null;

  const [workspaces, setWorkspaces] = useState<WorkspaceCardData[]>([]);
  const [location, setLocation] = useState(locationState?.searchLocation || "");
  const [locationCoords, setLocationCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [moveInDate, setMoveInDate] = useState<Date | null>(null);
  const [guestRange, setGuestRange] = useState<[number, number]>([
    1,
    maxGuests,
  ]);
  const [hasSearched, setHasSearched] = useState(false);

  // Filter states
  const [selectedWorkspaceType, setSelectedWorkspaceType] = useState<
    number | null
  >(null);
  const [capacityRange, setCapacityRange] = useState<[number, number]>([
    1,
    maxGuests,
  ]);
  const [selectedBookingType, setSelectedBookingType] = useState<number | null>(
    null
  );
  const [showFilters, setShowFilters] = useState(false);
  const [showMapView, setShowMapView] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 25;

  // Sort state
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleGuestChange = (event: Event, newValue: number | number[]) => {
    setGuestRange(newValue as [number, number]);
    console.log(event);
  };

  async function handleSearch(page: number = 1) {
    setHasSearched(true);
    setCurrentPage(page);

    const result = await SearchWorkspaces({
      location: location,
      moveInDate: moveInDate,
      capacity: guestRange[1],
      userId: null,
      workspaceTypeId: selectedWorkspaceType,
      minCapacity: capacityRange[0],
      maxCapacity: capacityRange[1],
      minimumBookingType: selectedBookingType,
      page: page,
      pageSize: pageSize,
      sortBy: sortBy,
      sortOrder: sortOrder,
    });

    if (result instanceof Error) {
      console.error(result.message);
      // Handle error
    } else {
      // Use the workspace data and apply client-side sorting
      console.log(result);
      const sortedWorkspaces = sortWorkspaces(result.workspaces);
      setWorkspaces(sortedWorkspaces);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      setCurrentPage(result.currentPage);
    }
  }

  function handlePageChange(page: number) {
    handleSearch(page);
  }

  function handleSortChange(sortField: string, order: "asc" | "desc") {
    setSortBy(sortField);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page when sorting changes
    handleSearch(1);
  }

  // Client-side sorting function with featured workspaces priority
  const sortWorkspaces = useCallback(
    (workspaces: WorkspaceCardData[]): WorkspaceCardData[] => {
      if (!sortBy) return workspaces;

      const sorted = [...workspaces].sort((a, b) => {
        // Check if either workspace is featured (assuming featured workspaces have a property or specific criteria)
        // For now, we'll assume workspaces with higher capacity or premium locations are featured
        const aIsFeatured = a.capacity >= 100 || a.pricePerYear >= 50000;
        const bIsFeatured = b.capacity >= 100 || b.pricePerYear >= 50000;

        // Featured workspaces always come first
        if (aIsFeatured && !bIsFeatured) return -1;
        if (!aIsFeatured && bIsFeatured) return 1;

        // If both or neither are featured, sort by selected criteria
        let comparison = 0;

        switch (sortBy) {
          case "price":
            comparison = a.pricePerYear - b.pricePerYear;
            break;
          case "date":
            // Since there's no date field in the interface, we'll sort by workspaceId as a proxy for creation date
            comparison = a.workspaceId - b.workspaceId;
            break;
          default:
            return 0;
        }

        return sortOrder === "asc" ? comparison : -comparison;
      });

      return sorted;
    },
    [sortBy, sortOrder]
  );

  function handleLocationSelect(address: AddressComponents): void {
    setLocation(address.fullAddress || "");
    if (address.location?.lat && address.location?.lng) {
      setLocationCoords({
        lat: address.location.lat,
        lng: address.location.lng,
      });
    }
  }

  const onRenderSearchWorkspaces = useCallback(async () => {
    setHasSearched(true); // Mark as searched for initial load
    const result = await SearchWorkspaces({
      location: "",
      moveInDate: null,
      capacity: null,
      userId: null,
      page: 1,
      pageSize: pageSize,
    });

    if (result instanceof Error) {
      console.error(result.message);
      // Handle error
    } else {
      // Use the workspace data and apply client-side sorting for initial load
      console.log(result);
      const sortedWorkspaces = sortWorkspaces(result.workspaces);
      setWorkspaces(sortedWorkspaces);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      setCurrentPage(result.currentPage);
    }
  }, [sortWorkspaces]);

  useEffect(() => {
    // If coming from home page with a search location, trigger search automatically
    if (locationState?.fromHomePage && locationState?.searchLocation) {
      // Use a small delay to ensure state is set
      const timer = setTimeout(() => {
        handleSearch();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Otherwise, execute default search with all null params on initial render
      onRenderSearchWorkspaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  return (
    <div className="min-h-screen container mx-auto px-4 py-8">
      {/* Main Content Area with Map Integration */}
      {((!hasSearched && workspaces.length > 0) || hasSearched) && (
        <div className="px-4 py-8">
          <div className="max-w-full mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 h-screen">
              {/* Left Column: Search Header + Workspace List Section */}
              <div
                className={`w-full  ${
                  !showMapView
                    ? "lg:w-1/2 lg:pr-3"
                    : "hidden lg:block lg:w-1/2 lg:pr-3"
                }`}
              >
                {/* Header with title and search */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-8">
                    <h1 className="text-4xl text-gray-900">
                      Find your dream workspace
                    </h1>
                    {/* Map View Toggle Button - Only visible on small screens */}
                    <button
                      className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => setShowMapView(!showMapView)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.158.69-.158 1.006 0l4.994 2.497c.317.158.69.158 1.007 0z"
                        />
                      </svg>
                      <span className="text-sm font-medium">
                        {showMapView ? "List View" : "Map View"}
                      </span>
                    </button>
                  </div>

                  {/* Search Bar */}
                  <div className="mb-6">
                    <div className="flex items-center rounded-2xl py-2 gap-2 border border-gray-300 hover:shadow-lg transition-shadow bg-white">
                      {isGoogleMapsLoaded ? (
                        <GoogleSearchBox
                          onPlaceSelected={handleLocationSelect}
                          initialValue={location}
                          onInputChange={(value) => setLocation(value)}
                        />
                      ) : (
                        <div className="flex flex-col w-100 gap-1 px-4 py-1 rounded-2xl">
                          <div className="text-md font-semibold">Location</div>
                          <input
                            type="text"
                            placeholder="Loading search..."
                            className="w-full focus:outline-none bg-transparent placeholder-gray-500 text-sm"
                            disabled
                          />
                        </div>
                      )}

                      {/* Right side buttons container */}
                      <div className="flex items-center gap-2 ml-auto px-4">
                        {/* Filter Button */}
                        <button
                          className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors ${
                            showFilters ||
                            selectedWorkspaceType ||
                            selectedBookingType ||
                            capacityRange[0] > 1 ||
                            capacityRange[1] < maxGuests ||
                            moveInDate ||
                            guestRange[0] > 1 ||
                            guestRange[1] < maxGuests
                              ? "bg-[var(--secondary)] text-white"
                              : "bg-white border-gray-300 hover:border-gray-400"
                          }`}
                          onClick={() => setShowFilters(!showFilters)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-4 h-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m0 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-5.25 0H7.5"
                            />
                          </svg>
                          <span className="text-sm font-medium">Filters</span>
                        </button>

                        {/* Search Button */}
                        <button
                          className="bg-[var(--primary)] hover:bg-[var(--secondary)] rounded-full p-2 px-4 cursor-pointer hover:cursor-pointer hover:text-white"
                          onClick={() => handleSearch()}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 640 640"
                            strokeWidth={1.5}
                            fill="currentColor"
                            className="w-8 h-8"
                          >
                            <path d="M480 272C480 317.9 465.1 360.3 440 394.7L566.6 521.4C579.1 533.9 579.1 554.2 566.6 566.7C554.1 579.2 533.8 579.2 521.3 566.7L394.7 440C360.3 465.1 317.9 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272zM272 416C351.5 416 416 351.5 416 272C416 192.5 351.5 128 272 128C192.5 128 128 192.5 128 272C128 351.5 192.5 416 272 416z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Filters Panel */}
                  {showFilters && (
                    <div className="mb-6 p-6 bg-white border border-gray-200 rounded-2xl shadow-sm">
                      {/* Move In Date and Who Section */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pb-6 border-b border-gray-200">
                        {/* Move In Date Filter */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Move In Date
                          </label>
                          <div className="relative z-10">
                            <DatePicker
                              selected={moveInDate}
                              onChange={(date) => setMoveInDate(date)}
                              selectsStart
                              startDate={moveInDate}
                              minDate={new Date()}
                              placeholderText="Select move in date"
                              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                              dateFormat="d MMM yyyy"
                              popperClassName="z-[9999]"
                              popperPlacement="bottom-start"
                              preventOpenOnFocus={false}
                              shouldCloseOnSelect={true}
                            />
                          </div>
                        </div>

                        {/* Who (Guest Range) Filter */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Number of Guests
                          </label>
                          <div className="px-2">
                            <AirbnbSlider
                              slots={{ thumb: AirbnbThumbComponent }}
                              getAriaLabel={(index) =>
                                index === 0
                                  ? "Minimum guests"
                                  : "Maximum guests"
                              }
                              value={guestRange}
                              onChange={handleGuestChange}
                              valueLabelDisplay="auto"
                              min={1}
                              max={maxGuests}
                              className="mb-2"
                            />
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>
                                {guestRange[0]} guest
                                {guestRange[0] !== 1 ? "s" : ""}
                              </span>
                              <span>
                                {guestRange[1]} guest
                                {guestRange[1] !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Workspace Type Filter */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Workspace Type
                          </label>
                          <select
                            value={selectedWorkspaceType || ""}
                            onChange={(e) =>
                              setSelectedWorkspaceType(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                          >
                            <option value="">All Types</option>
                            {workspaceTypes.slice(1).map((type) => (
                              <option key={type.value} value={type.value || ""}>
                                {type.title}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Capacity Range Filter */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Capacity Range
                          </label>
                          <div className="px-2">
                            <AirbnbSlider
                              slots={{ thumb: AirbnbThumbComponent }}
                              value={capacityRange}
                              onChange={(_, newValue) =>
                                setCapacityRange(newValue as [number, number])
                              }
                              valueLabelDisplay="auto"
                              min={1}
                              max={maxGuests}
                              className="mb-2"
                            />
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>{capacityRange[0]} people</span>
                              <span>{capacityRange[1]} people</span>
                            </div>
                          </div>
                        </div>

                        {/* Booking Type Filter */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Minimum Booking
                          </label>
                          <select
                            value={selectedBookingType || ""}
                            onChange={(e) =>
                              setSelectedBookingType(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                          >
                            <option value="">Any Duration</option>
                            <option value="0">Hours</option>
                            <option value="1">Days</option>
                            <option value="2">Weeks</option>
                            <option value="3">Months</option>
                            <option value="4">Years</option>
                          </select>
                        </div>
                      </div>

                      {/* Filter Actions */}
                      <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => {
                            setSelectedWorkspaceType(null);
                            setCapacityRange([1, maxGuests]);
                            setSelectedBookingType(null);
                            setMoveInDate(null);
                            setGuestRange([1, maxGuests]);
                          }}
                          className="text-sm text-gray-600 hover:text-gray-900 underline"
                        >
                          Clear all filters
                        </button>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowFilters(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              handleSearch();
                              setShowFilters(false);
                            }}
                            className="px-6 py-2 text-sm font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-yellow-500 transition-colors"
                          >
                            Apply Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sort Section */}
                {hasSearched && (
                  <div className="mb-6 flex justify-end">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700">
                        Sort by:
                      </span>
                      <select
                        value={`${sortBy}-${sortOrder}`}
                        onChange={(e) => {
                          const [field, order] = e.target.value.split("-");
                          if (field && order) {
                            handleSortChange(field, order as "asc" | "desc");
                          }
                        }}
                        className="px-3 py-2 border bg-white border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent text-sm"
                      >
                        <option value="-">Default</option>
                        <option value="price-asc">Price: Low to High</option>
                        <option value="price-desc">Price: High to Low</option>
                        <option value="date-asc">Date: Oldest First</option>
                        <option value="date-desc">Date: Newest First</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Workspace List */}
                <WorkspaceList workspaces={workspaces} />

                {/* Pagination or No More Results Message */}
                {hasSearched && (
                  <div className="mt-8 flex justify-center">
                    {totalPages <= 1 ? (
                      <p className="text-gray-500 text-center">
                        No more workspaces found
                      </p>
                    ) : (
                      <div className="flex items-center gap-2">
                        {/* Previous Button */}
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            currentPage <= 1
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-1">
                          {Array.from(
                            { length: Math.min(5, totalPages) },
                            (_, i) => {
                              let pageNumber;
                              if (totalPages <= 5) {
                                pageNumber = i + 1;
                              } else if (currentPage <= 3) {
                                pageNumber = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNumber = totalPages - 4 + i;
                              } else {
                                pageNumber = currentPage - 2 + i;
                              }

                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => handlePageChange(pageNumber)}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    currentPage === pageNumber
                                      ? "bg-[var(--primary)] text-white"
                                      : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            }
                          )}
                        </div>

                        {/* Next Button */}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            currentPage >= totalPages
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          Next
                        </button>

                        {/* Page Info */}
                        <span className="ml-4 text-sm text-gray-500">
                          Page {currentPage} of {totalPages} ({totalCount} total
                          results)
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Map Section */}
              <div
                className={`w-full ${
                  showMapView
                    ? "block lg:w-1/2 lg:pl-3"
                    : "hidden lg:block lg:w-1/2 lg:pl-3"
                }`}
              >
                <div
                  className={`sticky top-4 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden ${
                    showMapView
                      ? "h-[calc(100vh-12rem)] lg:h-[calc(100vh-8rem)]"
                      : "h-64 lg:h-[calc(100vh-8rem)]"
                  }`}
                >
                  {/* Map Container */}
                  <div className="h-full">
                    {isGoogleMapsLoaded ? (
                      <WorkspaceMap
                        center={locationCoords || undefined}
                        workspaces={workspaces}
                        onWorkspaceSelect={(workspace) => {
                          // Optional: Scroll to workspace in list or highlight it
                          console.log("Selected workspace:", workspace);
                        }}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-gray-500">Loading map...</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show featured workspaces even after search if user hasn't found what they're looking for */}
      {hasSearched && workspaces.length > 0 && (
        <div className="mt-20 border-t border-gray-200 pt-8">
          <div className="max-w-7xl mx-auto px-4 text-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Not finding what you're looking for?
            </h3>
            <p className="text-gray-600">
              Check out our featured workspaces for more options
            </p>
          </div>
          <FeaturedWorkspaces
            location={location}
            latitude={locationCoords?.lat}
            longitude={locationCoords?.lng}
          />
        </div>
      )}
    </div>
  );
}

export async function SearchWorkspaces(
  params: WorkspaceSearchParams
): Promise<PaginatedWorkspaceResponse | Error> {
  try {
    const api = axios.create({
      baseURL: "/",
    });
    // Construct the request payload with filters, pagination, and sorting
    const payload: WorkspaceSearchParams = {
      userId: params.userId,
      userOnly: params.userOnly || false,
      location: params.location,
      moveInDate: params.moveInDate ? params.moveInDate : null,
      capacity: params.capacity ? params.capacity : null,
      workspaceTypeId: params.workspaceTypeId,
      minCapacity: params.minCapacity,
      maxCapacity: params.maxCapacity,
      minimumBookingType: params.minimumBookingType,
      page: params.page || 1,
      pageSize: params.pageSize || 25,
      sortBy: params.sortBy || "",
      sortOrder: params.sortOrder || "asc",
    };

    const response = await api.post("/api/Workspace/listWorkspaces", payload);

    // Handle both array and object responses with pagination
    const data = response.data;

    // If the response is already paginated
    if (data && typeof data === "object" && "workspaces" in data) {
      return {
        workspaces: data.workspaces || [],
        totalCount: data.totalCount || 0,
        currentPage: data.currentPage || 1,
        totalPages: data.totalPages || 1,
        pageSize: data.pageSize || 25,
      } as PaginatedWorkspaceResponse;
    }

    // If the response is just an array (backward compatibility)
    const workspacesData = Array.isArray(data) ? data : [];
    return {
      workspaces: workspacesData as WorkspaceCardData[],
      totalCount: workspacesData.length,
      currentPage: 1,
      totalPages: 1,
      pageSize: workspacesData.length,
    } as PaginatedWorkspaceResponse;
  } catch (err) {
    console.error("Error fetching workspaces:", err);
    return new Error("Failed to load workspaces");
  }
}
